import { isAuthenticated, getAccessToken } from "utilities/authorization";
import { API_URL, ORIGIN } from "./appConfig";
import merge from "lodash/merge";
import getClientError from "utilities/getClientError";

const getOptions = (options) => {
  const defaultOptions = {
    headers: {
      "Accept-Language": localStorage.getItem("language"),
      "Content-Type": "application/json",
      "X-HostName": ORIGIN,
    },
  };

  // Only add Authorization if authenticated
  if (isAuthenticated()) {
    defaultOptions.headers.Authorization = `Bearer ${getAccessToken()}`;
  }

  return merge(defaultOptions, options);
};

const parseResponse = async (response) => {
  const contentType = response.headers.get("content-type") || "";

  // Return early for no-content responses
  if (response.status === 204) {
    return null;
  }

  // For both success and error responses, try to parse JSON first
  try {
    if (contentType.includes("application/json")) {
      const data = await response.json();
      // If it's not a successful response, throw with the parsed data
      if (!response.ok) {

        const errorText = getClientError(data);

        const error = new Error(errorText || "Request failed");
        error.data = data;
        error.status = response.status;
        error.statusText = response.statusText;
        throw error;
      }

      return data;
    }
  } catch (e) {
    // If it's already our error with data, rethrow it
    if (e.data) throw e;

    // Otherwise, create a new error with the text
    const errorText = await response.text();
    const error = new Error(
      errorText || response.statusText || "Request failed"
    );
    error.status = response.status;
    error.statusText = response.statusText;
    throw error;
  }

  // For non-JSON responses
  if (response.ok) {
    return response.text();
  }
  
  const errorText = await response.text();

  const error = new Error(errorText || "Request failed");
  error.status = response.status;
  error.statusText = response.statusText;
  throw error;
};

const parseData = (data, options) => {
  if (options.headers?.["Content-Type"]) return data;
  return JSON.stringify(data);
};

const api = {
  request:
    (method) =>
    async (url, data = null, options = {}) => {
      const fetchOptions = getOptions({
        ...options,
        method,
        ...(data && { body: parseData(data, options) }),
      });

      const response = await fetch(`${API_URL}${url}`, fetchOptions);
      return parseResponse(response);
    },
};

export default {
  get: api.request("GET"),
  post: api.request("POST"),
  put: api.request("PUT"),
  delete: api.request("DELETE"),
};
